import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Cta } from '../Cta';
import { Reveal } from '../Reveal';
import { useScore } from '../ScoreContext';
const earlyStages = ['Finds what applies', 'Chooses a useful next move', 'Shows what they need'];

/**
 * Reader-movement spine. Final stage is the message; services are supporting proof.
 */
function ReaderToWorkSpine() {
  const reduceMotion = useReducedMotion();
  return <div className="relative w-full max-w-sm" data-id="element-768" data-mp="1ngkor5qwsxmv">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-769" data-mp="1sdksha0adoat">
        When someone reads your book
      </p>

      {/* Line terminates at the final stage marker */}
      <div className="relative mt-5" data-id="element-770" data-mp="223dwnpy3n151">
        <div className="absolute bottom-0 left-[0.4375rem] top-[0.7rem] w-px bg-gradient-to-b from-seaglass from-[65%] to-sand" aria-hidden="true" data-id="element-771" data-mp="224fhun8tx40h" />

        <ol className="relative space-y-5" data-id="element-772" data-mp="19nctn43mdk0f">
          {earlyStages.map((label, i) => <motion.li key={label} className="grid grid-cols-[0.875rem_1fr] items-start gap-x-4" initial={reduceMotion ? false : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: reduceMotion ? 0 : 0.1 + i * 0.08,
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-773" data-mp="19l8grwnqtao0" data-mpi={i}>
              <span className="relative z-10 mt-[0.55rem] h-2 w-2 justify-self-center rounded-full bg-seaglass ring-[3px] ring-paper" aria-hidden="true" data-id="element-774" data-mp="m18qkv3ivmym" />
              <span className="font-serif text-lg leading-snug text-navy md:text-xl" data-id="element-775" data-mp="d0kdc2lydqms">
                {label}
              </span>
            </motion.li>)}
        </ol>

        <motion.div className="relative mt-5 grid grid-cols-[0.875rem_1fr] items-start gap-x-4" initial={reduceMotion ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: reduceMotion ? 0 : 0.38,
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-776" data-mp="843gmk0ou3y3">
          <span className="relative z-10 mt-[0.6rem] h-2.5 w-2.5 justify-self-center rounded-full bg-sand ring-[3px] ring-paper" aria-hidden="true" data-id="element-777" data-mp="3rwvrg4zvnjja" />
          <div data-id="element-778" data-mp="kkpfqhaj4ef">
            <p className="font-serif text-xl font-semibold leading-snug text-navy md:text-[1.35rem]" data-id="element-779" data-mp="3hyzlrspplgsk">
              Moves toward the right work
            </p>
            <p className="mt-2 max-w-[18rem] text-sm font-medium leading-snug text-sand-dark" data-id="element-780" data-mp="3mylvhs6bwewm">
              Coaching · Consulting · Speaking
              <br data-id="element-781" data-mp="jhc6rud9v9fu" />
              Training · Workshops
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}
export function HeroV2() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="relative overflow-hidden bg-paper px-6 pb-10 pt-10 md:px-10 md:pb-12 md:pt-12" data-id="element-782" data-mp="9iwcxjmv1m2n">
      <div className="mx-auto max-w-6xl" data-id="element-783" data-mp="2q7a9ydaxlqxp">
        <div className="mx-auto max-w-3xl text-center" data-id="element-784" data-mp="2q68n7bkjzg05">
          <Reveal data-id="element-785" data-mp="2u9wtzs1d27kc">
            <span className="inline-block rounded-full border border-black/[0.08] bg-white px-3.5 py-1 text-sm font-medium text-navy/80 shadow-sm" data-id="element-786" data-mp="2uag9l23jgjv7">
              For expert-authors
            </span>
          </Reveal>
          <Reveal delay={0.04} data-id="element-787" data-mp="2hq9kd2xhw37t">
            <h1 className="mt-5 font-serif text-[clamp(2.35rem,5vw,4rem)] leading-[1.04] tracking-[-0.02em] text-navy" data-id="element-788" data-mp="2ht5ml3gdle22">
              <span className="block" data-id="element-789" data-mp="2rofmnln7rbd7">You wrote the book.</span>
              <span className="mt-1.5 block italic text-[#2f4a42]" data-id="element-790" data-mp="2rmw2vl2cmi6o">
                We build what comes next.
              </span>
            </h1>
          </Reveal>
        </div>

        <div className="mx-auto mt-10 max-w-5xl border-t border-gray-200/70 pt-9 md:mt-12 md:pt-10" data-id="element-791" data-mp="1z1uwgoih55b8">
          <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.95fr] lg:gap-14 xl:gap-16" data-id="element-792" data-mp="2dqr3qjcpieas">
            <Reveal delay={0.08} data-id="element-793" data-mp="28x6b76tvuciz">
              <div data-id="element-794" data-mp="1l8ws4co202vj">
                <p className="max-w-xl text-lg font-semibold leading-snug text-navy md:text-xl" data-id="element-795" data-mp="1l83kl9h9t9cc">
                  Your book gives readers the ideas.
                </p>
                <p className="mt-2.5 max-w-xl text-base leading-relaxed text-text-muted md:text-lg" data-id="element-796" data-mp="jif3trxwjoaj">
                  We build the path that helps them use those ideas—and gives you
                  more ways to use the book across your business.
                </p>
                <div className="mt-6 flex w-fit max-w-full flex-col items-stretch gap-2" data-id="element-797" data-mp="shsegnxriiwu">
                  <Cta variant="primary" onClick={openDiagnostic} className="min-h-[52px] px-6 py-3.5 text-base font-semibold md:text-lg" data-id="element-798" data-mp="xbei5qly7cgi">
                    Get Your Reader-to-Revenue Score
                  </Cta>
                  <p className="max-w-[16.5rem] self-center text-center text-sm leading-snug text-navy/65" data-id="element-799" data-mp="17a41lntht4yv">
                    8 questions to see how clearly reader interest connects to
                    the right work.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} data-id="element-800" data-mp="3d32fvo8sd90d">
              <ReaderToWorkSpine data-id="element-801" data-mp="3hzizq68yd17n" />
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}